import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Button from 'components/Button/Button';
import { createFragmentContainer, graphql } from 'react-relay';
import { BackgroundCheckCard_delegate } from './__generated__/BackgroundCheckCard_delegate.graphql';
import { BackgroundCheckCard_manager } from './__generated__/BackgroundCheckCard_manager.graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdBadge,
  faRotateRight,
  faShieldCheck as duoShield,
  faClipboardCheck,
  faSpinner
} from '@fortawesome/pro-duotone-svg-icons';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import RequestBackgroundCheckModal, {
  CheckType
} from 'features/background-checks/components/RequestBackgroundCheckModal/RequestBackgroundCheckModal';
import moment, { Moment } from 'moment';
import {
  faChevronRight,
  faShieldHalved
} from '@fortawesome/pro-solid-svg-icons';
import AutomatedCheckModal from 'features/background-checks/components/AutomatedCheckModal/AutomatedCheckModal';

const GREY = '#6A728F';
const AMBER = '#f68f1f';
const GREEN = '#1FD426';

const useStyles = createUseStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem'
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    margin: 0
  },
  noBackgroundCheck: {
    color: GREY,
    fontSize: '0.8rem'
  },
  pending: {
    color: '#69728f',
    fontSize: '0.8rem'
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  emptyPanel: {
    marginTop: 12,
    padding: '26px 20px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px'
  },
  emptyShield: {
    fontSize: 28,
    color: GREY
  },
  panel: {
    marginTop: 12,
    padding: '18px 20px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  panelClickable: {
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: '#c9cedb',
      boxShadow: '0 2px 8px rgba(58, 63, 88, 0.06)'
    }
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontSize: '0.8rem',
    color: '#3A3F58',
    fontWeight: 500
  },
  rowIcon: {
    width: 18,
    textAlign: 'center',
    marginTop: 1
  },
  rowSub: {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: GREY,
    marginTop: 2
  },
  rowDivider: {
    height: 1,
    background: '#ededed',
    margin: '2px 0'
  },
  viewPending: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#0b57ff',
    marginTop: 2
  }
}));

type Props = {
  delegate: BackgroundCheckCard_delegate;
  manager: BackgroundCheckCard_manager;
  onRequestBackgroundCheck(
    includesDBS: boolean,
    checkType: CheckType,
    idExpiry?: Moment
  ): void;
  onViewPendingCheck?: () => void;
};

function BackgroundCheckCard({
  delegate,
  manager,
  onRequestBackgroundCheck,
  onViewPendingCheck
}: Props) {
  const classes = useStyles();
  const action = useActionMenuDispatch();

  const [bgOpen, setBgOpen] = React.useState(false);
  const onRequest = () => setBgOpen(true);

  const requestModal = (
    <RequestBackgroundCheckModal
      isOpen={bgOpen}
      renewalCost={manager.company.renewalPennyPrice}
      managerName={`${manager.firstName} ${manager.lastName}`}
      companyName={manager.company.name}
      delegate={{
        uuid: delegate.uuid,
        firstName: delegate.firstName,
        lastName: delegate.lastName,
        email: delegate.email,
        profileImageUrl: delegate.profileImageUrl,
        hasActiveBackgroundCheck: delegate.hasActiveBackgroundCheck
      }}
      onRequest={(
        _delegateUUID: string,
        checked: boolean,
        checkType: CheckType,
        idDate?: Moment
      ) => {
        onRequestBackgroundCheck(checked, checkType, idDate);
        setBgOpen(false);
      }}
      onClose={() => setBgOpen(false)}
      checkCost={manager.company.backgroundCheckPennyPrice}
      dbsCost={manager.company.dbsPennyPrice}
      dbsOnlyCost={manager.company.dbsOnlyPennyPrice}
      checksLeft={
        (manager.company?.invoicing?.backgroundChecksPrePurchased ?? 0) -
        (manager.company.invoicing?.backgroundChecksUsed ?? 0)
      }
      dbsLeft={
        (manager.company.invoicing?.dbsPrePurchased ?? 0) -
        (manager.company.invoicing?.dbsUsed ?? 0)
      }
      renewalsLeft={
        (manager.company.invoicing?.renewalsPrePurchased ?? 0) -
        (manager.company.invoicing?.renewalsUsed ?? 0)
      }
    />
  );

  const onUploadExisting = (evt: any) => {
    action({
      type: 'show',
      children: (
        <AutomatedCheckModal
          userName="test"
          delegateUUID={delegate.uuid}
          onClose={() => {
            action({ type: 'hide' });
            window.location.reload();
          }}
          successPennyPrice={manager.company.verificationSuccessPennyPrice}
          failurePennyPrice={manager.company.verificationFailurePennyPrice}
        />
      ),
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'modal'
      }
    });
  };

  const checks = delegate.backgroundChecks ?? [];

  // Pending = not yet approved; a delegate can have several in flight
  const pendingChecks = checks.filter((c) => !c.approvalDate);
  const pendingInProgress = pendingChecks.filter((c) => !c.isVerification);
  const pendingVerifications = pendingChecks.filter((c) => c.isVerification);

  // Approved checks split by expiry; only the most recently expired is shown
  const isExpired = (check: typeof checks[number]) =>
    !!check.expiresAt && moment(check.expiresAt).isSameOrBefore(moment());
  const approvedChecks = checks.filter((c) => !!c.approvalDate);
  const activeChecks = approvedChecks.filter((c) => !isExpired(c));
  const latestExpired = approvedChecks
    .filter(isExpired)
    .reduce<typeof checks[number] | null>(
      (latest, check) =>
        !latest || moment(check.expiresAt!).isAfter(moment(latest.expiresAt!))
          ? check
          : latest,
      null
    );

  const checkText = (check: typeof checks[number]) =>
    check.onlyDBS
      ? 'DBS check'
      : check.isVerification
      ? 'background check (verified)'
      : `${check.isCAA ? 'CAA' : 'Non-CAA'} background check${
          check.dbsExpiryDate ? ' with DBS' : ''
        }`;

  const activeLabel = (check: typeof checks[number]) =>
    `Active ${checkText(check)}`;

  const activeIcon = (check: typeof checks[number]) =>
    check.onlyDBS
      ? faIdBadge
      : check.isVerification
      ? faClipboardCheck
      : duoShield;

  const activeDates = (check: typeof checks[number]) => {
    const parts: string[] = [];
    if (check.idExpiryDate) {
      parts.push(`ID expires ${moment(check.idExpiryDate).format('DD/MM/YYYY')}`);
    }
    if (check.dbsExpiryDate) {
      parts.push(
        `DBS expires ${moment(check.dbsExpiryDate).format('DD/MM/YYYY')}`
      );
    }
    return parts.join(' · ');
  };

  const pendingLabel = (check: typeof checks[number]) =>
    check.isRenewal
      ? 'Renewal pending'
      : check.onlyDBS
      ? 'DBS check pending'
      : 'Background check pending';

  const pendingIcon = (check: typeof checks[number]) =>
    check.isRenewal ? faRotateRight : check.onlyDBS ? faIdBadge : duoShield;

  // The only things a manager can add to speed a pending check up
  const pendingNeeds = (check: typeof checks[number]) => {
    const needs: string[] = [];
    if (
      check.isCAA &&
      !check.isRenewal &&
      !check.onlyDBS &&
      !check.interviewNotes
    ) {
      needs.push('interview notes');
    }
    if (!check.idExpiryDate) {
      needs.push('ID expiry');
    }
    if (!check.dbsExpiryDate) {
      needs.push('DBS expiry');
    }
    return needs;
  };

  const hasAnything =
    activeChecks.length > 0 || pendingChecks.length > 0 || !!latestExpired;
  // Verifications aren't shown on the pending checks tab, so no click-through
  const clickable = pendingInProgress.length > 0 && !!onViewPendingCheck;

  return (
    <div>
      {requestModal}
      <div>
        <div className={classes.header}>
          <p className={classes.subtitle}>
            Background check{' '}
            {pendingChecks.length > 0 && (
              <span className={classes.pending}>(pending)</span>
            )}
          </p>
          {activeChecks.length > 0 && pendingChecks.length === 0 && (
            <Button archetype="flat" onClick={onRequest}>
              <FontAwesomeIcon icon={faRotateRight} /> Request renewal
            </Button>
          )}
        </div>

        {!hasAnything && (
          <div className={classes.emptyPanel}>
            <FontAwesomeIcon
              icon={faShieldHalved}
              className={classes.emptyShield}
            />
            <p className={classes.noBackgroundCheck}>
              No TTC background check present
            </p>
            <div className={classes.buttons}>
              <Button archetype="flat" onClick={onUploadExisting}>
                <FontAwesomeIcon icon={faClipboardCheck} color="#0957ff" />{' '}
                Upload existing check
              </Button>
              <Button archetype="flatPrimary" onClick={onRequest}>
                <FontAwesomeIcon icon={duoShield} /> Request background check
              </Button>
            </div>
          </div>
        )}

        {hasAnything && (
          <div
            className={`${classes.panel} ${
              clickable ? classes.panelClickable : ''
            }`}
            onClick={clickable ? onViewPendingCheck : undefined}
          >
            {activeChecks.map((check) => {
              const dates = activeDates(check);
              return (
                <div key={check.uuid} className={classes.row}>
                  <FontAwesomeIcon
                    icon={activeIcon(check)}
                    className={classes.rowIcon}
                    style={
                      {
                        color: GREEN,
                        '--fa-primary-color': GREEN,
                        '--fa-secondary-color': GREEN
                      } as any
                    }
                  />
                  <div>
                    {activeLabel(check)}
                    {dates && <div className={classes.rowSub}>{dates}</div>}
                  </div>
                </div>
              );
            })}

            {latestExpired && (
              <div className={classes.row}>
                <FontAwesomeIcon
                  icon={activeIcon(latestExpired)}
                  className={classes.rowIcon}
                  style={
                    {
                      color: GREY,
                      '--fa-primary-color': GREY,
                      '--fa-secondary-color': GREY
                    } as any
                  }
                />
                <div>
                  {`Expired ${checkText(latestExpired)}`}
                  <div className={classes.rowSub}>
                    Expired{' '}
                    {moment(latestExpired.expiresAt!).format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>
            )}

            {(activeChecks.length > 0 || latestExpired) &&
              pendingChecks.length > 0 && <div className={classes.rowDivider} />}

            {pendingInProgress.map((check) => {
              const needs = pendingNeeds(check);
              return (
                <div key={check.uuid} className={classes.row}>
                  <FontAwesomeIcon
                    icon={pendingIcon(check)}
                    className={classes.rowIcon}
                    style={
                      {
                        color: AMBER,
                        '--fa-primary-color': AMBER,
                        '--fa-secondary-color': AMBER
                      } as any
                    }
                  />
                  <div>
                    {pendingLabel(check)}
                    <div className={classes.rowSub}>
                      {needs.length > 0
                        ? `Needs ${needs.join(', ')}`
                        : 'Nothing needed from you right now'}
                    </div>
                  </div>
                </div>
              );
            })}

            {pendingVerifications.map((check) => (
              <div key={check.uuid} className={classes.row}>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className={classes.rowIcon}
                  style={
                    {
                      color: '#0b57ff',
                      '--fa-primary-color': '#0b57ff',
                      '--fa-secondary-color': '#0b57ff'
                    } as any
                  }
                />
                Verification processing
              </div>
            ))}

            {activeChecks.length === 0 && pendingChecks.length === 0 && (
              <div className={classes.buttons} style={{ marginTop: 8 }}>
                <Button archetype="flat" onClick={onUploadExisting}>
                  <FontAwesomeIcon icon={faClipboardCheck} color="#0957ff" />{' '}
                  Upload existing check
                </Button>
                <Button archetype="flatPrimary" onClick={onRequest}>
                  <FontAwesomeIcon icon={duoShield} /> Request background
                  check
                </Button>
              </div>
            )}

            {clickable && (
              <div className={classes.viewPending}>
                View pending checks <FontAwesomeIcon icon={faChevronRight} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const BackgroundCheckCardFrag = createFragmentContainer(BackgroundCheckCard, {
  delegate: graphql`
    fragment BackgroundCheckCard_delegate on Delegate {
      uuid
      firstName
      lastName
      email
      profileImageUrl
      backgroundCheckPending
      hasActiveBackgroundCheck
      backgroundChecks {
        uuid
        approvalDate
        expiresAt
        isRenewal
        isVerification
        isCAA
        idExpiryDate
        dbsExpiryDate
        onlyDBS
        interviewNotes {
          yourName
        }
      }
    }
  `,
  manager: graphql`
    fragment BackgroundCheckCard_manager on Manager {
      firstName
      lastName
      company {
        backgroundCheckPennyPrice
        dbsPennyPrice
        dbsOnlyPennyPrice
        renewalPennyPrice
        name
        verificationSuccessPennyPrice
        verificationFailurePennyPrice
        invoicing {
          dbsUsed
          dbsPrePurchased
          backgroundChecksUsed
          backgroundChecksPrePurchased
          renewalsUsed
          renewalsPrePurchased
        }
      }
    }
  `
});

export default BackgroundCheckCardFrag;
